import React, { useMemo, useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DatePicker from 'react-datepicker';
import { 
  FaMapMarkerAlt, 
  FaLocationArrow,
  FaCalendarAlt, 
  FaSearch, 
  FaFilter,
  FaHistory,
  FaTimes,
  FaHotel,
  FaHome,
  FaUmbrellaBeach,
  FaMountain,
  FaBriefcase,
  FaChevronDown,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import GuestPetSelector from './GuestPetSelector';
import MeetingRoomSearch from './MeetingRoomSearch';
import AiAssistantInlineBar, { AiAssistantTriggerButton } from './AiAssistantInlineBar';
import VoiceMicButton from './VoiceMicButton';
import useVoiceBooking from '../hooks/useVoiceBooking';
import { parseVoiceCommand, intentToSearchState } from '../utils/voiceSearchCommands';
import LocationPermissionModal from './LocationPermissionModal';
import { getApiUrl } from '../config/api';
import {
  getLocationPermissionPreference,
  resolveNearbyLocationLabel,
  setLocationPermissionPreference,
} from '../utils/locationPermission';
import { buildStoredSearchPayload } from '../utils/searchStorage';
import {
  getCategoryQueryValue,
  hotelMatchesPropertyType,
  normalizePropertyTypeId,
} from '../utils/propertyTypeFilter';
import { MOBILE_MAX_WIDTH } from '../utils/breakpoints';
import 'react-datepicker/dist/react-datepicker.css';
import './GlobalSearch.css';

const MOBILE_BREAKPOINT = MOBILE_MAX_WIDTH;

const formatFieldDate = (date) => {
  if (!date) return 'Add dates';
  try {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return 'Add dates';
  }
};

const formatLocalYMD = (dateLike) => {
  const d = new Date(dateLike);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const GlobalSearch = () => {
  const navigate = useNavigate();
  
  // Core search state
  const [location, setLocation] = useState('');
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guestData, setGuestData] = useState({
    adults: 1,
    children: 0,
    infants: 0,
    rooms: 1,
    pets: 0,
  });
  
  // Enhanced search features
  const [propertyType, setPropertyType] = useState('all');
  const [priceRange, setPriceRange] = useState([0, 50000]);
  const [showFilters, setShowFilters] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [voiceProcessing, setVoiceProcessing] = useState(false);
  const [smartLoading, setSmartLoading] = useState(false);
  const [smartError, setSmartError] = useState('');
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  
  // Single active panel (mobile: isolate inputs, unmount inactive dropdowns)
  const [activePanel, setActivePanel] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  // Mobile: Airbnb-style collapsed single search bar that expands on tap
  const [mobileExpanded, setMobileExpanded] = useState(false);
  
  // Refs
  const searchInputRef = useRef(null);
  const containerRef = useRef(null);
  
  // Property types - Enhanced for Indian market
  const propertyTypes = [
    { id: 'all', label: 'All Properties', icon: FaSearch },
    { id: 'hotel', label: 'Hotels', icon: FaHotel },
    { id: 'guest house', label: 'Guest Houses', icon: FaHome },
    { id: 'pg', label: 'PG (Paying Guest)', icon: FaMountain },
    { id: 'villa', label: 'Villa', icon: FaHome },
    { id: 'apartment', label: 'Apartments', icon: FaMountain },
    { id: 'meeting room', label: 'Meeting Rooms', icon: FaBriefcase }
  ];
  
  // Popular destinations - Enhanced Indian cities
  const popularDestinations = [
    'Mangalore', 'Bangalore', 'Mumbai', 'Delhi', 'Goa', 'Chennai', 
    'Hyderabad', 'Pune', 'Kochi', 'Mysore', 'Udupi', 'Manipal',
    'Hassan', 'Shimoga', 'Hubli', 'Dharwad', 'Belgaum', 'Hampi',
    'Kolkata', 'Ahmedabad', 'Jaipur', 'Lucknow', 'Nagpur', 'Indore',
    'Bhopal', 'Vadodara', 'Agra', 'Varanasi', 'Srinagar', 'Jodhpur',
    'Amritsar', 'Coimbatore', 'Madurai', 'Chandigarh', 'Thiruvananthapuram',
    'Bhubaneswar', 'Dehradun', 'Udaipur', 'Rishikesh', 'Manali', 'Shimla'
  ];

  const suggestedDestinations = [
    {
      id: 'nearby',
      title: 'Nearby',
      subtitle: "Find what\u2019s around you",
      value: 'Nearby',
      icon: FaLocationArrow,
      theme: 'blue',
    },
    {
      id: 'north-goa',
      title: 'North Goa, Goa',
      subtitle: 'Popular beach destination',
      value: 'North Goa, Goa',
      icon: FaUmbrellaBeach,
      theme: 'gold',
    },
    {
      id: 'south-goa',
      title: 'South Goa, Goa',
      subtitle: 'For sights like Basilica of Bom Jesus',
      value: 'South Goa, Goa',
      icon: FaUmbrellaBeach,
      theme: 'green',
    },
    {
      id: 'hyderabad',
      title: 'Hyderabad, Telangana',
      subtitle: 'For its top-notch dining',
      value: 'Hyderabad, Telangana',
      icon: FaHotel,
      theme: 'gray',
    },
    {
      id: 'puducherry',
      title: 'Puducherry, Puducherry',
      subtitle: 'For its seaside allure',
      value: 'Puducherry, Puducherry',
      icon: FaUmbrellaBeach,
      theme: 'coral',
    },
    {
      id: 'ooty',
      title: 'Ooty, Tamil Nadu',
      subtitle: 'Great for a weekend getaway',
      value: 'Ooty, Tamil Nadu',
      icon: FaMountain,
      theme: 'sand',
    },
  ];
  
  // ✅ SECURITY: Get user-specific recent searches key
  const getRecentSearchesKey = () => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userObj = JSON.parse(user);
        return `recentSearches_${userObj._id || userObj.id}`;
      }
    } catch (error) {
      console.error('Error getting user ID:', error);
    }
    return 'recentSearches'; // Fallback for non-logged-in users
  };

  // Load recent searches on mount
  useEffect(() => {
    const recentSearchesKey = getRecentSearchesKey();
    const stored = localStorage.getItem(recentSearchesKey);
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (e) {
        console.error('Error loading recent searches:', e);
      }
    }
  }, []);

  // Mobile detection for popup isolation
  useEffect(() => {
    const check = () => setIsMobile(typeof window !== 'undefined' && window.innerWidth <= MOBILE_BREAKPOINT);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Close location panel on outside click (mobile: single active panel)
  useEffect(() => {
    if (activePanel !== 'location') return;
    const handle = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
        setActivePanel(null);
      }
    };
    document.addEventListener('mousedown', handle);
    document.addEventListener('touchstart', handle, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('touchstart', handle);
    };
  }, [activePanel]);
  
  // Auto-suggestions with enhanced matching
  const handleLocationChange = (value) => {
    setLocation(value);
    
    if (value.length > 1) {
      // Enhanced filtering for better city matches
      const filtered = popularDestinations.filter(dest =>
        dest.toLowerCase().includes(value.toLowerCase()) ||
        dest.toLowerCase().startsWith(value.toLowerCase())
      ).slice(0, 8); // Limit to 8 suggestions
      
      setSuggestions(filtered);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  };
  
  // ✅ SECURITY: Save recent search with user-specific key
  const saveRecentSearch = (searchData) => {
    const newSearch = {
      id: Date.now(),
      location: searchData.location,
      checkIn: searchData.checkIn,
      checkOut: searchData.checkOut,
      guests: searchData.guests,
      propertyType: searchData.propertyType,
      timestamp: new Date().toISOString()
    };
    
    const updated = [newSearch, ...recentSearches.filter(s => s.location !== searchData.location)].slice(0, 5);
    setRecentSearches(updated);
    const recentSearchesKey = getRecentSearchesKey();
    localStorage.setItem(recentSearchesKey, JSON.stringify(updated));
  };

  const toYMD = (dateObj) => {
    if (!dateObj) return null;
    try {
      return formatLocalYMD(dateObj);
    } catch {
      return null;
    }
  };

  const ymdToDate = (ymd) => {
    if (!ymd) return null;
    // Avoid timezone shifting by setting local midnight
    return new Date(`${ymd}T00:00:00`);
  };

  const getLastSearchKey = () => {
    try {
      const user = localStorage.getItem('user');
      if (user) {
        const userObj = JSON.parse(user);
        return `lastSearch_${userObj._id || userObj.id}`;
      }
    } catch (error) {
      console.error('Error getting user ID:', error);
    }
    return 'lastSearch';
  };

  const runHotelSearch = async ({ location: loc, checkInYmd, checkOutYmd, guests, propertyType, priceRange }) => {
    try {
      const queryParams = new URLSearchParams({
        q: loc || '',
        category: getCategoryQueryValue(propertyType),
        limit: '300',
        minPrice: priceRange[0],
        maxPrice: priceRange[1],
      });
      if (checkInYmd) queryParams.set('checkIn', checkInYmd);
      if (checkOutYmd) queryParams.set('checkOut', checkOutYmd);
      if (guests?.adults) queryParams.set('adults', String(guests.adults));
      
      const url = `${getApiUrl('/api/search')}?${queryParams.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server returned non-JSON response (${response.status}): ${text.substring(0, 100)}`);
      }
      
      const json = await response.json();
      
      if (!response.ok || !json?.success) {
        throw new Error(json?.message || `Hotel search failed (${response.status})`);
      }

      // Enhanced search results with filters
      const enhancedResults = buildStoredSearchPayload(json, loc, {
        checkIn: checkInYmd,
        checkOut: checkOutYmd,
        guests,
        propertyType,
        priceRange,
      });

      localStorage.setItem('searchResults', JSON.stringify(enhancedResults));
      navigate('/search');
    } catch (err) {
      console.error('Hotel search error:', err);
      throw err;
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setSmartError('');
    setShowSuggestions(false);
    setActivePanel(null);

    if (!String(location || '').trim()) {
      setSmartError('Please enter a destination');
      return;
    }

    if (propertyType === 'meeting room') {
      const params = new URLSearchParams({ location: location.trim() });
      if (guestData?.adults) params.set('participants', String(guestData.adults));
      navigate(`/meetings/search-results?${params.toString()}`);
      return;
    }

    try {
      setSmartLoading(true);

      const searchData = {
        location,
        checkIn: toYMD(checkIn),
        checkOut: toYMD(checkOut),
        guests: guestData,
        propertyType,
        priceRange
      };

      const lastSearchKey = getLastSearchKey();
      localStorage.setItem(lastSearchKey, JSON.stringify({
        ...searchData,
        updatedAt: new Date().toISOString(),
      }));

      // Save to recent searches
      saveRecentSearch(searchData);

      // Perform search
      await runHotelSearch({
        location,
        checkInYmd: toYMD(checkIn),
        checkOutYmd: toYMD(checkOut),
        guests: guestData,
        propertyType,
        priceRange
      });

      console.log('✅ Enhanced search completed');
    } catch (err) {
      const message = String(err?.message || err || '');
      
      if (message.includes('non-JSON response') || message.includes('<!DOCTYPE')) {
        setSmartError('Server error: Please check if the backend server is running.');
      } else if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
        setSmartError('Network error: Unable to connect to the server.');
      } else {
        setSmartError(`Search failed: ${message.substring(0, 100)}`);
      }

      console.error('Search error:', err);
    } finally {
      setSmartLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setLocation(suggestion);
    setShowSuggestions(false);
    // Guided flow: after place selection, open check-in calendar automatically.
    setActivePanel('checkIn');
  };

  const applyNearbyLocation = async ({ persistAlways = false } = {}) => {
    setSmartError('');
    setNearbyLoading(true);

    try {
      const nearbyLabel = await resolveNearbyLocationLabel();
      setLocation(nearbyLabel);
      setShowSuggestions(false);
      setActivePanel('checkIn');

      if (persistAlways) {
        setLocationPermissionPreference('always');
      }
    } catch (error) {
      const message = String(error?.message || error || 'Could not access your location.');
      setSmartError(message);
    } finally {
      setNearbyLoading(false);
      setShowLocationModal(false);
    }
  };

  const handleNearbyClick = async () => {
    setShowSuggestions(false);
    setSmartError('');

    const preference = getLocationPermissionPreference();
    if (preference === 'denied') {
      setSmartError('Location access is blocked. Choose a city manually or reset permission in browser settings.');
      setActivePanel(null);
      return;
    }

    if (preference === 'always') {
      await applyNearbyLocation();
      return;
    }

    setShowLocationModal(true);
    setActivePanel(null);
  };

  const handleDestinationClick = (destination) => {
    if (destination.id === 'nearby') {
      handleNearbyClick();
      return;
    }

    handleSuggestionClick(destination.value);
  };

  const handleRecentSearchClick = (recentSearch) => {
    setLocation(recentSearch.location);
    setCheckIn(ymdToDate(recentSearch.checkIn));
    setCheckOut(ymdToDate(recentSearch.checkOut));
    setGuestData(recentSearch.guests);
    setPropertyType(recentSearch.propertyType || 'all');
    setShowSuggestions(false);
    setActivePanel(null);
  };

  const handleGuestApply = (values) => {
    setGuestData(values);
  };

  const popupOpenMobile = isMobile && activePanel != null;
  const isMeetingRoomSearch = propertyType === 'meeting room';
  const showCollapsedMobileSearch = isMobile && !mobileExpanded && !isMeetingRoomSearch;

  const totalGuestCount = (guestData.adults || 0) + (guestData.children || 0);
  const collapsedDateText =
    checkIn && checkOut
      ? `${formatFieldDate(checkIn)} – ${formatFieldDate(checkOut)}`
      : 'Any dates';
  const collapsedGuestText = `${totalGuestCount} guest${totalGuestCount > 1 ? 's' : ''} · ${guestData.rooms} room${
    guestData.rooms > 1 ? 's' : ''
  }`;
  const collapsedSummary = `${collapsedDateText} · ${collapsedGuestText}`;

  const expandMobileSearch = () => {
    setMobileExpanded(true);
    setActivePanel('location');
    setShowSuggestions(true);
    setTimeout(() => searchInputRef.current?.focus(), 60);
  };

  const collapseMobileSearch = () => {
    setMobileExpanded(false);
    setActivePanel(null);
    setShowSuggestions(false);
  };

  const stayPropertyTypes = propertyTypes.filter((type) => type.id !== 'meeting room');
  const meetingPropertyType = propertyTypes.find((type) => type.id === 'meeting room');
  const closeAllPanels = () => {
    setActivePanel(null);
    setShowSuggestions(false);
  };

  const resolveSearchLocation = () => {
    const current = String(location || '').trim();
    if (current) return current;
    try {
      const last = JSON.parse(localStorage.getItem(getLastSearchKey()) || '{}');
      return String(last.location || '').trim();
    } catch {
      return '';
    }
  };

  const handlePropertyTypeSelect = async (nextType) => {
    setPropertyType(nextType);
    closeAllPanels();
    setSmartError('');

    if (nextType === 'meeting room') return;

    const loc = resolveSearchLocation();
    if (!loc) return;

    if (!String(location || '').trim()) {
      setLocation(loc);
    }

    try {
      setSmartLoading(true);
      const searchData = {
        location: loc,
        checkIn: toYMD(checkIn),
        checkOut: toYMD(checkOut),
        guests: guestData,
        propertyType: nextType,
        priceRange,
      };
      saveRecentSearch(searchData);
      localStorage.setItem(getLastSearchKey(), JSON.stringify({
        ...searchData,
        updatedAt: new Date().toISOString(),
      }));
      await runHotelSearch({
        location: loc,
        checkInYmd: toYMD(checkIn),
        checkOutYmd: toYMD(checkOut),
        guests: guestData,
        propertyType: nextType,
        priceRange,
      });
    } catch (err) {
      const message = String(err?.message || err || '');
      setSmartError(`Search failed: ${message.substring(0, 100)}`);
    } finally {
      setSmartLoading(false);
    }
  };

  const speakRef = useRef(() => {});

  const executeVoiceSearch = async (parsed, lang = 'en') => {
    const state = intentToSearchState(parsed);
    const loc = state.location || parsed?.transcript || '';

    if (state.maxBudget) {
      setPriceRange([0, state.maxBudget]);
    }
    if (state.guests?.adults) {
      setGuestData((prev) => ({ ...prev, adults: state.guests.adults }));
    }
    if (state.checkIn) setCheckIn(ymdToDate(state.checkIn));
    if (state.checkOut) setCheckOut(ymdToDate(state.checkOut));

    if (state.propertyType === 'meeting room' || parsed?.bookingType === 'meeting') {
      setPropertyType('meeting room');
      if (loc) {
        const params = new URLSearchParams({ location: loc });
        if (state.guests?.adults) params.set('participants', String(state.guests.adults));
        navigate(`/meetings/search-results?${params.toString()}`);
        speakRef.current(
          parsed?.reply ||
            (lang === 'kn'
              ? `${loc} ನಲ್ಲಿ ಮೀಟಿಂಗ್ ರೂಮ್ ಹುಡುಕುತ್ತಿದ್ದೇನೆ`
              : lang === 'hi'
                ? `${loc} में मीटिंग रूम खोज रहा हूँ`
                : `Searching meeting rooms in ${loc}`)
        );
      }
      return;
    }

    if (!loc) {
      if (parsed?.reply) speakRef.current(parsed.reply);
      setAiAssistantOpen(true);
      return;
    }

    setLocation(loc);
    if (state.propertyType && state.propertyType !== 'all') {
      setPropertyType(state.propertyType);
    }

    const searchPayload = {
      location: loc,
      checkInYmd: state.checkIn || toYMD(checkIn),
      checkOutYmd: state.checkOut || toYMD(checkOut),
      guests: state.guests || guestData,
      propertyType: state.propertyType || propertyType,
      priceRange: state.maxBudget ? [0, state.maxBudget] : priceRange,
    };

    saveRecentSearch({
      location: loc,
      checkIn: searchPayload.checkInYmd,
      checkOut: searchPayload.checkOutYmd,
      guests: searchPayload.guests,
      propertyType: searchPayload.propertyType,
      priceRange: searchPayload.priceRange,
    });

    await runHotelSearch(searchPayload);
    speakRef.current(
      parsed?.reply ||
        (lang === 'kn'
          ? `${loc} ನಲ್ಲಿ ಸ್ಟೇಗಳನ್ನು ಹುಡುಕುತ್ತಿದ್ದೇನೆ`
          : lang === 'hi'
            ? `${loc} में स्टे खोज रहा हूँ`
            : `Searching stays in ${loc}`)
    );
  };

  const voice = useVoiceBooking({
    onTranscript: async (transcript, meta) => {
      setSmartError('');
      setVoiceProcessing(true);
      try {
        const parsed = await parseVoiceCommand(transcript, meta?.language || 'en');
        if (!parsed) return;
        await executeVoiceSearch(parsed, meta?.language || 'en');
      } catch (err) {
        setSmartError('Voice search failed. Please try again or type your search.');
        console.error('Voice search error:', err);
      } finally {
        setVoiceProcessing(false);
      }
    },
    onError: (code) => {
      if (code === 'not_supported') {
        setSmartError('Voice search is not supported in this browser. Use Chrome or Edge.');
      }
    },
  });

  speakRef.current = voice.speak;

  return (
    <motion.section 
      ref={containerRef}
      className={`global-search${popupOpenMobile ? ' global-search-popup-open-mobile' : ''}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
    >
      <div className="search-container">
        {smartError && (
          <motion.div 
            className="search-smart-error"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
          >
            {smartError}
          </motion.div>
        )}
        
        {/* Property Type Selector */}
        <div className="property-type-selector">
          {stayPropertyTypes.map((type) => {
            const IconComponent = type.icon;
            return (
              <button
                key={type.id}
                type="button"
                className={`property-type-btn ${propertyType === type.id ? 'active' : ''}`}
                onClick={() => handlePropertyTypeSelect(type.id)}
              >
                <IconComponent className="property-type-icon" />
                <span>{type.label}</span>
              </button>
            );
          })}

          {meetingPropertyType && (
            <button
              key={meetingPropertyType.id}
              type="button"
              className={`property-type-btn ${propertyType === meetingPropertyType.id ? 'active' : ''}`}
              onClick={() => handlePropertyTypeSelect(meetingPropertyType.id)}
            >
              {React.createElement(meetingPropertyType.icon, { className: 'property-type-icon' })}
              <span>{meetingPropertyType.label}</span>
            </button>
          )}

          <AiAssistantTriggerButton
            compact
            active={aiAssistantOpen}
            onClick={() => setAiAssistantOpen((open) => !open)}
          />
        </div>

        {aiAssistantOpen && (
          <div className="global-search-ai-panel">
            <AiAssistantInlineBar
              variant="home"
              panelOnly
              expanded
              voiceLanguage={voice.language}
              onVoiceLanguageChange={voice.setLanguage}
              onClose={() => setAiAssistantOpen(false)}
            />
          </div>
        )}

        {isMeetingRoomSearch ? (
          <div className="search-main-card">
            <MeetingRoomSearch
              voiceLanguage={voice.language}
              onVoiceLanguageChange={voice.setLanguage}
            />
          </div>
        ) : showCollapsedMobileSearch ? (
          <div className="search-main-card search-collapsed-card">
            <button
              type="button"
              className="mobile-search-pill"
              onClick={expandMobileSearch}
              aria-label="Open search"
            >
              <span className="mobile-search-pill-icon">
                <FaSearch />
              </span>
              <span className="mobile-search-pill-text">
                <span className="mobile-search-pill-title">
                  {location || 'Where are you going?'}
                </span>
                <span className="mobile-search-pill-sub">{collapsedSummary}</span>
              </span>
            </button>
          </div>
        ) : (
          <div className="search-main-card">
            {isMobile && (
              <div className="mobile-search-expanded-head">
                <span className="mobile-search-expanded-title">Search</span>
                <button
                  type="button"
                  className="mobile-search-collapse-btn"
                  onClick={collapseMobileSearch}
                  aria-label="Close search"
                >
                  <FaTimes />
                </button>
              </div>
            )}
            {(voice.interimTranscript || voiceProcessing) && (
              <p className="voice-interim-text" role="status">
                {voiceProcessing ? 'Processing your voice request…' : voice.interimTranscript}
              </p>
            )}

          <form className="search-form" onSubmit={handleSearch}>
          <div className="search-fields-row">
          <div className={`search-field-box location-field voice-search-field${activePanel === 'location' ? ' active-field' : ''}`}>
            <div className="field-icon-wrap" aria-hidden="true">
              <FaMapMarkerAlt />
            </div>
            <div className="field-text-wrap">
              <span className="field-label">Where are you going?</span>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search city, property or landmark"
                value={location}
                onChange={(e) => handleLocationChange(e.target.value)}
                onFocus={() => { setActivePanel('location'); setShowSuggestions(true); }}
                className="search-input field-value-input"
                aria-expanded={activePanel === 'location' && showSuggestions}
                aria-haspopup="listbox"
                aria-label="Where are you going?"
              />
            </div>
            <VoiceMicButton
              isListening={voice.isListening}
              isSpeaking={voice.isSpeaking}
              isSupported={voice.isSupported}
              language={voice.language}
              onToggle={voice.toggleListening}
              onStopSpeaking={voice.stopSpeaking}
              size="sm"
              variant="ghost"
              disabled={voiceProcessing || smartLoading}
            />
            {/* Suggestions Dropdown - only mount when location is the active panel (mobile: no overlap) */}
            <AnimatePresence>
              {activePanel === 'location' && showSuggestions && (suggestions.length > 0 || recentSearches.length > 0 || location.length === 0) && (
                <motion.div
                  className="suggestions-dropdown"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                >
                  {/* Show popular destinations when no input */}
                  {location.length === 0 && (
                    <div className="suggestions-section suggestions-section--destinations">
                      <h4>Suggested destinations</h4>
                      {suggestedDestinations.map((destination) => {
                        const Icon = destination.icon;
                        return (
                        <button
                          key={destination.id}
                          type="button"
                          className={`suggestion-item suggested-destination-item suggested-destination-item--${destination.theme}`}
                          onClick={() => handleDestinationClick(destination)}
                        >
                          <span className="suggested-destination-icon">
                            <Icon />
                          </span>
                          <span className="suggested-destination-content">
                            <span className="suggested-destination-title">{destination.title}</span>
                            <span className="suggested-destination-subtitle">{destination.subtitle}</span>
                          </span>
                        </button>
                        );
                      })}
                    </div>
                  )}
                  
                  {suggestions.length > 0 && (
                    <div className="suggestions-section">
                      <h4>Matching Destinations</h4>
                      {suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          type="button"
                          className="suggestion-item"
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          <FaMapMarkerAlt />
                          <span>{suggestion}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  {recentSearches.length > 0 && (
                    <div className="suggestions-section">
                      <h4>Recent Searches</h4>
                      {recentSearches.map((recent) => (
                        <button
                          key={recent.id}
                          type="button"
                          className="suggestion-item recent-search"
                          onClick={() => handleRecentSearchClick(recent)}
                        >
                          <FaHistory />
                          <div className="recent-search-content">
                            <span className="recent-location">{recent.location}</span>
                            <span className="recent-dates">
                              {recent.checkIn && recent.checkOut 
                                ? `${new Date(recent.checkIn).toLocaleDateString()} - ${new Date(recent.checkOut).toLocaleDateString()}`
                                : 'Flexible dates'
                              }
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <div
            className={`search-field-box date-field${activePanel === 'checkIn' ? ' active-field' : ''}`}
            onClick={() => { setActivePanel('checkIn'); setShowSuggestions(false); }}
          >
            <div className="field-icon-wrap" aria-hidden="true">
              <FaCalendarAlt />
            </div>
            <div className="field-text-wrap">
              <span className="field-label">Check in</span>
              <span className={`field-value${checkIn ? '' : ' field-value--placeholder'}`}>
                {formatFieldDate(checkIn)}
              </span>
              <DatePicker
                selected={checkIn}
                onChange={(date) => {
                  setCheckIn(date);
                  if (checkOut && date && checkOut <= date) {
                    setCheckOut(null);
                  }
                  setActivePanel('checkOut');
                }}
                onCalendarOpen={() => { setActivePanel('checkIn'); setShowSuggestions(false); }}
                onCalendarClose={() => setActivePanel((p) => (p === 'checkIn' ? null : p))}
                open={activePanel === 'checkIn'}
                placeholderText="Add dates"
                dateFormat="MMM dd, yyyy"
                minDate={new Date()}
                className="search-input date-input field-hidden-datepicker"
                wrapperClassName="date-picker-wrapper"
                popperClassName="global-search-datepicker-popper global-search-datepicker-popper-checkin"
              />
            </div>
            <FaChevronDown className="field-chevron" aria-hidden="true" />
          </div>

          <div
            className={`search-field-box date-field${activePanel === 'checkOut' ? ' active-field' : ''}`}
            onClick={() => { setActivePanel('checkOut'); setShowSuggestions(false); }}
          >
            <div className="field-icon-wrap" aria-hidden="true">
              <FaCalendarAlt />
            </div>
            <div className="field-text-wrap">
              <span className="field-label">Check out</span>
              <span className={`field-value${checkOut ? '' : ' field-value--placeholder'}`}>
                {formatFieldDate(checkOut)}
              </span>
              <DatePicker
                selected={checkOut}
                onChange={(date) => {
                  setCheckOut(date);
                  setActivePanel(null);
                }}
                onCalendarOpen={() => { setActivePanel('checkOut'); setShowSuggestions(false); }}
                onCalendarClose={() => setActivePanel((p) => (p === 'checkOut' ? null : p))}
                open={activePanel === 'checkOut'}
                placeholderText="Add dates"
                dateFormat="MMM dd, yyyy"
                minDate={checkIn || new Date()}
                className="search-input date-input field-hidden-datepicker"
                wrapperClassName="date-picker-wrapper"
                popperClassName="global-search-datepicker-popper global-search-datepicker-popper-checkout"
              />
            </div>
            <FaChevronDown className="field-chevron" aria-hidden="true" />
          </div>

          <div className={`search-field-box guest-field${activePanel === 'guests' ? ' active-field' : ''}`}>
            <GuestPetSelector 
              onApply={handleGuestApply}
              initialValues={guestData}
              isOpen={activePanel === 'guests'}
              variant="card"
              onOpenChange={(open) => {
                setActivePanel(open ? 'guests' : null);
                if (!open) setShowSuggestions(false);
              }}
            />
          </div>
          </div>

          <button type="submit" className="search-button search-button--full" disabled={smartLoading}>
            <FaSearch className="search-btn-icon" />
            <span>{smartLoading ? 'Searching…' : 'Search Properties'}</span>
          </button>
          </form>
          </div>
        )}

        {/* Advanced Filters */}
        <AnimatePresence>
          {!isMeetingRoomSearch && showFilters && (
            <motion.div
              className="advanced-filters"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
            >
              <div className="filter-section">
                <label>Price Range (per night)</label>
                <div className="price-range">
                  <input
                    type="range"
                    min="0"
                    max="50000"
                    step="500"
                    value={priceRange[0]}
                    onChange={(e) => setPriceRange([parseInt(e.target.value), priceRange[1]])}
                    className="price-slider"
                  />
                  <input
                    type="range"
                    min="0"
                    max="50000"
                    step="500"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                    className="price-slider"
                  />
                  <div className="price-display">
                    ₹{priceRange[0].toLocaleString()} - ₹{priceRange[1].toLocaleString()}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <LocationPermissionModal
        isOpen={showLocationModal}
        loading={nearbyLoading}
        onClose={() => {
          if (!nearbyLoading) setShowLocationModal(false);
        }}
        onAllowAlways={() => applyNearbyLocation({ persistAlways: true })}
        onAllowOnce={() => applyNearbyLocation()}
        onNeverAllow={() => {
          setLocationPermissionPreference('denied');
          setShowLocationModal(false);
          setSmartError('Location access is blocked for this site.');
        }}
      />
    </motion.section>
  );
};

export default GlobalSearch;

